/**
 * Devoirs de l'élève (prof particulier ET classe) pour l'Accueil — lecture
 * seule, aucune écriture.
 *
 * ⚠️ `finish_attempt` ne passe PAS `assignments.status` à `completed` (même
 * constat que le Feed, `use-news-feed.ts`) : un devoir est considéré fait dès
 * qu'une tentative TERMINÉE existe sur l'exercice depuis qu'il a été donné.
 * Helpers PURS testés (`homework.test.ts`).
 */

import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

export interface HomeworkChapter {
  title: string;
  subject: string;
  level: string;
  slug: string;
}

export interface Homework {
  id: string;
  exerciseId: string;
  title: string;
  chapter: HomeworkChapter | null;
  dueAt: string | null;
  assignedAt: string | null;
  /** Nom du prof (devoir individuel) ou de la classe. */
  fromLabel: string;
  source: 'tutor' | 'class';
}

export interface CompletedAttemptRef {
  exercise_id: string | null;
  completed_at: string | null;
}

export type DueState = 'late' | 'today' | 'soon' | 'later' | 'none';

const DAY = 86_400_000;

/** Minuit local du jour de `d`. */
function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Nombre de jours calendaires (locaux) entre aujourd'hui et l'échéance (négatif = passée). */
export function daysUntil(dueAt: string, now: Date = new Date()): number {
  return Math.round((startOfDay(new Date(dueAt)) - startOfDay(now)) / DAY);
}

/**
 * État d'échéance : en retard (échéance passée), aujourd'hui, bientôt (≤ 2 jours),
 * plus tard, ou sans date.
 */
export function dueState(dueAt: string | null, now: Date = new Date()): DueState {
  if (!dueAt) return 'none';
  const d = daysUntil(dueAt, now);
  if (d < 0) return 'late';
  if (d === 0) return 'today';
  if (d <= 2) return 'soon';
  return 'later';
}

/** « en retard depuis hier », « pour aujourd'hui », « pour demain », « pour samedi 10 oct. ». */
export function dueLabel(dueAt: string | null, now: Date = new Date()): string | null {
  if (!dueAt) return null;
  const d = daysUntil(dueAt, now);
  if (d < 0) {
    if (d === -1) return 'En retard depuis hier';
    return `En retard depuis ${-d} jours`;
  }
  if (d === 0) return 'Pour aujourd’hui';
  if (d === 1) return 'Pour demain';
  return `Pour ${new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'short' }).format(new Date(dueAt))}`;
}

/** Devoir déjà fait : tentative terminée sur l'exercice depuis qu'il a été donné. */
export function isHomeworkDone(
  hw: Pick<Homework, 'exerciseId' | 'assignedAt'>,
  completed: readonly CompletedAttemptRef[],
): boolean {
  const since = hw.assignedAt ? new Date(hw.assignedAt).getTime() : null;
  return completed.some(
    (a) =>
      a.exercise_id === hw.exerciseId &&
      (since === null || !a.completed_at || new Date(a.completed_at).getTime() >= since),
  );
}

/** Rang de tri : en retard → aujourd'hui → bientôt → plus tard → sans date. */
const DUE_RANK: Record<DueState, number> = { late: 0, today: 1, soon: 2, later: 3, none: 4 };

/** Devoirs restant à faire, les plus urgents d'abord. */
export function pendingHomework(
  list: readonly Homework[],
  completed: readonly CompletedAttemptRef[],
  now: Date = new Date(),
): Homework[] {
  return list
    .filter((hw) => !isHomeworkDone(hw, completed))
    .sort((a, b) => {
      const ra = DUE_RANK[dueState(a.dueAt, now)];
      const rb = DUE_RANK[dueState(b.dueAt, now)];
      if (ra !== rb) return ra - rb;
      if (a.dueAt && b.dueAt) return a.dueAt.localeCompare(b.dueAt);
      return (a.assignedAt ?? '').localeCompare(b.assignedAt ?? '');
    });
}

// ── Lecture ──────────────────────────────────────────────────────────────────

type RawAssignment = {
  id: string;
  exercise_id: string;
  class_id: string | null;
  due_at: string | null;
  assigned_at: string | null;
  exercises: { title: string | null; chapters: HomeworkChapter | null } | null;
  profiles: { display_name: string | null } | null;
  classes: { name: string | null } | null;
};

export const studentHomeworkKey = (studentId: string) => ['student-homework', studentId] as const;

/** Devoirs restant à faire (individuels + classe), triés par urgence. */
export function useStudentHomework(studentId: string | undefined) {
  return useQuery({
    queryKey: studentHomeworkKey(studentId ?? ''),
    enabled: !!studentId,
    // Court : au retour d'un exercice, l'Accueil doit refléter le devoir fait.
    staleTime: 10_000,
    queryFn: async (): Promise<Homework[]> => {
      const db = supabase as unknown as SupabaseClient;
      const { data, error } = await db
        .from('assignments')
        .select(
          `id, exercise_id, class_id, due_at, assigned_at,
           exercises ( title, chapters ( title, subject, level, slug ) ),
           profiles!assignments_tutor_id_fkey ( display_name:visible_name ),
           classes ( name )`,
        )
        .eq('student_id', studentId!)
        .in('status', ['pending', 'in_progress']);
      if (error) throw error;
      const rows = (data ?? []) as unknown as RawAssignment[];
      if (rows.length === 0) return [];

      const exerciseIds = [...new Set(rows.map((r) => r.exercise_id))];
      const { data: done } = await db
        .from('attempts')
        .select('exercise_id, completed_at')
        .eq('student_id', studentId!)
        .eq('status', 'completed')
        .in('exercise_id', exerciseIds);

      const list: Homework[] = rows.map((r) => ({
        id: r.id,
        exerciseId: r.exercise_id,
        title: r.exercises?.title ?? 'Exercice',
        chapter: r.exercises?.chapters ?? null,
        dueAt: r.due_at,
        assignedAt: r.assigned_at,
        fromLabel: r.class_id
          ? (r.classes?.name ?? 'Ma classe')
          : (r.profiles?.display_name ?? 'Ton prof'),
        source: r.class_id ? 'class' : 'tutor',
      }));
      return pendingHomework(list, (done ?? []) as CompletedAttemptRef[]);
    },
  });
}
